import { ActionIcon, Container, Flex, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconEdit, IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useAccount } from 'hooks/auth'
import { useDeleteWork, useWorks } from 'hooks/work'
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  useMantineReactTable,
} from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useMemo, useState } from 'react'
import { Account } from 'types/auth'
import { OrderPlace, SewingWorkType, UpdateWork, WorkTableItem, WorkType } from 'types/work'
import { getOrderPlaceName, getSewingWorkTypeName } from 'utils'

type Props = {
  workType: WorkType
  users?: Array<Account>
}

export default function WorkTable({ users, workType }: Props) {
  const deleteWork = useDeleteWork()
  function openUpdateWorkModal(work: UpdateWork) {
    modals.openContextModal({
      modal: 'updateWork',
      centered: true,
      title: 'Edytuj wykonaną pracę',
      innerProps: {
        initialValues: work,
      },
    })
  }

  function openDeleteWorkModal(work: WorkTableItem) {
    modals.openConfirmModal({
      title: 'Usuń wykonaną pracę',
      centered: true,
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć <b>{work.amount}</b> metrów wykonanej pracy dla zamówienia{' '}
          <b>{work.orderNumber}</b>? Nie będzie można cofnąć tej operacji.
        </Text>
      ),
      labels: { confirm: 'Usuń wykonaną pracę', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: async () => await deleteWork.mutateAsync({ id: work.id, workType: work.workType }),
    })
  }

  function openAddWorkModal() {
    modals.openContextModal({
      modal: 'addWork',
      centered: true,
      title: 'Dodaj wykonaną pracę',
      innerProps: {},
    })
  }

  const columns = useMemo<Array<MRT_ColumnDef<WorkTableItem>>>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Data dodania',
        filterVariant: 'date',
        Cell: ({ cell }) => {
          const createdAt = cell.getValue<Date>()
          return dayjs(createdAt).format('D MMMM HH:mm')
        },
        mantineFilterDateInputProps: {
          placeholder: 'Filtruj po dacie dodania',
          locale: 'pl',
          valueFormat: 'D MMMM',
          preserveTime: false,
        },
      },
      {
        accessorKey: 'orderNumber',
        header: 'Numer zamówienia',
      },
      {
        accessorKey: 'amount',
        header: 'Ilość metrów',
      },
      {
        accessorKey: 'place',
        header: 'Miejsce',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: Object.values(OrderPlace).map((place) => ({
            value: place,
            label: getOrderPlaceName(place),
          })),
        },
        Cell: ({ cell }) => {
          const place = cell.getValue<OrderPlace>()
          return getOrderPlaceName(place)
        },
      },
      ...(workType === WorkType.SEWING
        ? [
            {
              accessorKey: 'workType',
              header: 'Rodzaj szycia',
              filterVariant: 'select',
              mantineFilterSelectProps: {
                data: Object.values(SewingWorkType).map((workType) => ({
                  value: workType,
                  label: getSewingWorkTypeName(workType),
                })),
              },
              Cell: ({ cell }) => {
                const workType = cell.getValue<SewingWorkType>()
                return getSewingWorkTypeName(workType)
              },
            } as MRT_ColumnDef<WorkTableItem>,
          ]
        : []),
      ...(users
        ? [
            {
              accessorKey: 'userId',
              header: 'Użytkownik',
              filterVariant: 'select',
              mantineFilterSelectProps: {
                data: users.map((user) => ({
                  value: user.id,
                  label: `${user.firstName} ${user.lastName}`,
                })),
              },
              Cell: ({ cell }) => {
                const userId = cell.getValue<string>()
                const user = users.find((user) => user.id === userId)
                return `${user?.firstName} ${user?.lastName}`
              },
            } as MRT_ColumnDef<WorkTableItem>,
          ]
        : []),
      {
        accessorKey: 'comments',
        header: 'Uwagi',
      },
    ],
    []
  )

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  })

  const { data: account } = useAccount()

  const workTypes =
    workType === WorkType.SEWING ? [WorkType.SEWING_DOUBLE_NEEDLE, WorkType.SEWING_LOCKSTITCH] : [workType]

  const works = useWorks(users === undefined ? account.id : undefined, {
    workTypes,
    columnFilters,
    globalFilter,
    pagination,
    sorting,
  })
  const isLoading = works.some((work) => work.isLoading)
  const isError = works.some((work) => work.isError)
  const isFetching = works.some((work) => work.isFetching)
  const error = works.find((work) => work.isError)?.error
  const refetch = () => {
    works.forEach((work) => work.refetch())
  }

  const memoizedData = useMemo(() => {
    const allData = works.map((r) => r.data).filter((r) => r !== undefined)

    if (allData.length === 0) {
      return { data: [], total: 0 }
    }

    return {
      data: allData.flatMap((r) => r.data),
      total: allData.reduce((sum, r) => sum + r.total, 0),
    }
  }, [JSON.stringify(works.map((r) => r.data))])

  const table = useMantineReactTable({
    columns,
    data: memoizedData.data,
    layoutMode: 'grid',
    rowCount: memoizedData.total,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      sorting,
    },
    initialState: {
      showGlobalFilter: true,
      showColumnFilters: true,
    },
    positionGlobalFilter: 'left',
    mantineSearchTextInputProps: {
      placeholder: `Wyszukaj po numerze zamówienia`,
      styles: { input: { minWidth: '300px' } },
    },
    enableGlobalFilterModes: true,
    enableSorting: true,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableColumnFilters: true,
    enableBottomToolbar: true,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    autoResetPageIndex: false,
    renderTopToolbarCustomActions: () => (
      <Container mr={0} pr={0}>
        <Tooltip label="Dodaj metry">
          <ActionIcon size="lg" variant="subtle" onClick={() => openAddWorkModal()} ml="auto" mr={4}>
            <IconPlus />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Odśwież dane">
          <ActionIcon size="lg" variant="subtle" onClick={() => refetch()}>
            <IconRefresh />
          </ActionIcon>
        </Tooltip>
      </Container>
    ),
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: `Wystąpił błąd podczas pobierania danych. (${error?.response?.data.message})`,
        }
      : undefined,
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    mantinePaginationProps: {
      rowsPerPageOptions: ['5', '10', '15'],
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 100,
      },
    },
    renderRowActions: ({ row }) => (
      <Flex gap="sm" justify="end">
        <Tooltip label="Edytuj">
          <ActionIcon variant="light" color="gray" radius="xl" onClick={() => openUpdateWorkModal(row.original)}>
            <IconEdit size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Usuń">
          <ActionIcon radius="xl" variant="light" color="red" onClick={() => openDeleteWorkModal(row.original)}>
            <IconTrash size={18} />
          </ActionIcon>
        </Tooltip>
      </Flex>
    ),
  })

  return <MantineReactTable table={table} />
}
